import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { isJwtExpired } from '@shared/helpers/token-expiry.helper';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { environment } from '@environment/environment';

interface ImpClaim {
  by_user_id: string;
  by_email: string;
  session_id: string;
  started_at: number;
}

interface RecruiterTokenPayload {
  exp: number;
  recruiter_id?: string;
  imp?: ImpClaim;
}

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  // A support session that is still live: the banner renders and counts down
  // off this flag, and hitting zero exits back to the admin app.
  isImpersonating = false;
  // The stored token carries an `imp` claim, expired or not. Kept separate so
  // an expired support token can still route an admin back to the admin app
  // on a 401 without reviving the banner.
  hasImpersonationToken = false;
  impersonationData: { impersonator_email: string; started_at: number } | null = null;
  expiresAt = 0;

  constructor(private http: HttpClient, private envService: CommonEnvironmentsService) {}

  init(): void {
    const token = this.envService.getToken();
    if (!token) return;
    let p: RecruiterTokenPayload;
    try { p = jwtDecode<RecruiterTokenPayload>(token); } catch { return; }
    if (!p?.imp) return;
    this.hasImpersonationToken = true;
    this.impersonationData = { impersonator_email: p.imp.by_email, started_at: p.imp.started_at };
    this.expiresAt = p.exp;
    // An already-expired token is a finished support session, not an active
    // one. The banner is mounted at the root of every app — including the
    // public token-link forms that need no login — so treating a dead token
    // as live flashes the banner and immediately force-exits to the admin
    // app. Leave the flag off: the page renders as plain logged-out instead.
    // A session that expires while the tab is open still exits through the
    // banner's own countdown, which only runs when init() saw a live token.
    if (isJwtExpired(p.exp)) return;
    this.isImpersonating = true;
  }

  // Indirection so specs can spy on navigation; window.location itself is not
  // mockable in modern browsers.
  redirect(url: string): void {
    window.location.assign(url);
  }

  async exit(): Promise<void> {
    const token = this.envService.getToken();
    let recruiterId = '';
    if (token) {
      try { recruiterId = jwtDecode<RecruiterTokenPayload>(token).recruiter_id || ''; } catch { /* ignore */ }
    }
    try {
      await this.http
        .post(`${environment.ENDPOINTS.BACK_END.V3}/admin/exit-impersonation`, {})
        .toPromise();
    } catch { /* best-effort */ }
    this.envService.cleanStorage();
    this.redirect(`${environment.ENDPOINTS.FRONT_END.ADMIN}/recruiters/${recruiterId}`);
  }
}
